import type { ComponentType } from 'react';
import type { IUser, AvailableCountries } from 'types/users.types';
import type { SwanOnboardingStepIdType } from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboardingStepIds';
import type {
  BankingSignupAccountDetails,
  BirthInfo,
} from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboarding.types';
import type { ProgressSegment } from 'components/organisms/OnboardingStepLayout';
import type { SwanActivationStepIdType } from '../ActivationFlow/constants/activationStepIds';
import type { StepComponentName } from '../ActivationFlow/constants/componentNames';
import type { CountryAlpha3Code } from 'types/countries';

/**
 * Step mode enum - determines whether step is before or after account creation
 */
export enum StepMode {
  PreAccount = 'pre_account',
  PostAccount = 'post_account',
}

export type StepStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

/**
 * Main step grouping - represents a high-level step in the flow
 * Can contain multiple substeps
 */
export interface MainStepDefinition {
  id: string;
  label: string; // For progress list display
  listIcon: ComponentType<{ className?: string }>;
  substeps: SubstepDefinition[];
  countrySpecific?: AvailableCountries[];
}

/**
 * Substep - individual screen/component within a main step
 */
export interface SubstepDefinition {
  id: string;
  mode: StepMode;
  onboardingStep?: SwanOnboardingStepIdType; // For pre-account step tracking (v2)
  activationStep?: SwanActivationStepIdType; // For post-account steps with backend tracking

  // Component to render (referenced by name to avoid circular dependencies)
  componentName: StepComponentName;

  // Props factory - generates props for the component
  // Receives flow state and handlers
  // todo: recognize to get rid of any type
  renderProps: (context: StepRenderContext) => any;

  // Metadata
  titleKey?: string; // Translation key for modal title
  subtitleKey?: string; // Translation key for modal subtitle
  modalIcon?: ComponentType<{ className?: string }>; // Optional icon for this specific substep
  analyticsEvent?: string; // Amplitude event to log

  // Flags
  isBookmark?: boolean; // Whether this is a major milestone
  requiresAuth?: boolean; // Whether user must be authenticated
  countrySpecific?: AvailableCountries[];
}

/**
 * Context passed to renderProps factory.
 * Contains all state and handlers needed by step components.
 */
export interface StepRenderContext {
  // Navigation handlers
  onContinue: () => void;
  onBack: () => void;
  onSkip?: (targetSubstepId: string) => void; // Skip to a specific substep

  // Progress information (for smart progress bar)
  progressSegments?: ProgressSegment[];
  onProgressSegmentClick?: (segmentId: string) => void;
  currentStepNumber?: number;
  totalSteps?: number;

  // Step metadata (automatically injected by system)
  title?: string; // Translated titleKey
  subtitle?: string; // Translated subtitleKey
  icon?: ComponentType<{ className?: string }>; // From substep's modalIcon

  // Loading states
  isUpdatingStep?: boolean; // True when backend step update is in progress

  // Pre-account state
  onboardingId: string;
  setOnboardingId: (id: string) => void;
  accountDetails?: BankingSignupAccountDetails;
  setAccountDetails: (details: BankingSignupAccountDetails) => void;
  proofOfRegistrationFiles: { url: string; payload: string; name?: string }[];
  setProofOfRegistrationFiles: React.Dispatch<
    React.SetStateAction<{ url: string; payload: string; name?: string }[]>
  >;
  birthDetails?: BirthInfo;
  setBirthDetails: (details: BirthInfo) => void;
  residencyDetails?: {
    street: string;
    city: string;
    zip: string;
    /** ISO 3166-1 alpha-3 uppercase (API enum shape) */
    country?: CountryAlpha3Code;
  };
  setResidencyDetails: (details: {
    street: string;
    city: string;
    zip: string;
    country?: CountryAlpha3Code;
  }) => void;

  // API mutations
  // todo: consider remove any type
  onboardingAPI: any;
  createBankConnectionAsync: any;

  /**
   * Onboarding PATCH deduplication (see `stepDefinitions.helpers.ts`):
   * JSON-stringified snapshot of the body from the **last successful** PATCH, after
   * stripping `_id`. One value for the whole unified flow session (not per substep).
   */
  lastPersistedPayload?: string;
  setLastPersistedPayload: (payload: string) => void;

  // User data
  user?: IUser;
  t: (key: string, options?: any) => string;
}
